import { useCallback, useEffect, useState } from 'react'
import { del, get, post } from '../api/client.ts'

/**
 * Suscripción a Web Push en iOS.
 *
 * Los dos requisitos que hacen fracasar la mayoría de intentos, comprobados en
 * la fase 0:
 *
 *  1. La app tiene que estar abierta desde el icono de la pantalla de inicio.
 *     En una pestaña de Safari `pushManager.subscribe()` falla y no llega nada,
 *     sin ningún error visible para el usuario.
 *  2. `Notification.requestPermission()` debe invocarse dentro del gesto del
 *     usuario. Si antes hay un `await` de otra cosa, iOS pierde el gesto y
 *     descarta la petición en silencio — por eso `enable()` lo llama lo primero.
 */

export interface PushState {
  supported: boolean
  /** true si hay que añadir la app a la pantalla de inicio antes de suscribirse. */
  needsHomeScreen: boolean
  permission: NotificationPermission | 'unsupported'
  subscribed: boolean
  busy: boolean
  error: string | null
}

function isStandalone(): boolean {
  return (
    (window.navigator as { standalone?: boolean }).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  )
}

function isIos(): boolean {
  const ua = navigator.userAgent
  // Un iPad moderno se identifica como Mac; los puntos táctiles lo delatan.
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1)
}

/**
 * La exigencia de estar en la pantalla de inicio es específica de iOS. En otros
 * navegadores la push funciona en una pestaña normal, y bloquearla ahí sólo
 * estorbaría al probar desde un portátil.
 */
function needsHomeScreen(): boolean {
  return isIos() && !isStandalone()
}

/**
 * Convierte la clave VAPID pública (base64url) al búfer que espera
 * `pushManager.subscribe`.
 *
 * Se reserva un ArrayBuffer explícito en vez de usar `Uint8Array.from`: la API
 * exige un búfer respaldado por ArrayBuffer, y la forma corta produce un tipo
 * que también admite SharedArrayBuffer y no encaja.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalised)
  const view = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i += 1) view[i] = raw.charCodeAt(i)
  return view
}

async function currentSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null
  const registration = await navigator.serviceWorker.getRegistration()
  return registration?.pushManager.getSubscription() ?? null
}

/**
 * Registra la suscripción en el servidor. Se llama también al arrancar la app:
 * la suscripción se pierde al reinstalar la PWA, y reenviarla es lo que lo
 * autorrepara sin que nadie tenga que hacer nada.
 */
export async function syncSubscription(): Promise<boolean> {
  try {
    if (Notification.permission !== 'granted') return false
    const subscription = await currentSubscription()
    if (!subscription) return false
    const json = subscription.toJSON() as { endpoint?: string; keys?: Record<string, string> }
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return false
    await post('/api/push/subscribe', {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    })
    return true
  } catch {
    return false
  }
}

export function usePush() {
  const [state, setState] = useState<PushState>({
    supported: 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window,
    needsHomeScreen: needsHomeScreen(),
    permission: 'Notification' in window ? Notification.permission : 'unsupported',
    subscribed: false,
    busy: false,
    error: null,
  })

  const refresh = useCallback(async () => {
    const subscription = await currentSubscription()
    setState((s) => ({
      ...s,
      needsHomeScreen: needsHomeScreen(),
      permission: 'Notification' in window ? Notification.permission : 'unsupported',
      subscribed: Boolean(subscription),
    }))
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const enable = useCallback(async () => {
    setState((s) => ({ ...s, busy: true, error: null }))
    try {
      // Primera instrucción del gesto: cualquier await previo perdería el
      // permiso en iOS.
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        setState((s) => ({
          ...s,
          busy: false,
          permission,
          error:
            permission === 'denied'
              ? 'Has denegado los avisos. Actívalos en Ajustes → Notificaciones → Tareas.'
              : 'No se ha concedido el permiso.',
        }))
        return
      }

      const registration = await navigator.serviceWorker.ready
      const { publicKey } = await get<{ publicKey: string }>('/api/push/config')
      if (!publicKey) throw new Error('El servidor no tiene configuradas las claves de notificación')

      let subscription = await registration.pushManager.getSubscription()
      subscription ??= await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      })

      const json = subscription.toJSON() as { endpoint: string; keys: Record<string, string> }
      await post('/api/push/subscribe', {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      })

      await refresh()
      setState((s) => ({ ...s, busy: false }))
    } catch (error) {
      setState((s) => ({
        ...s,
        busy: false,
        error: error instanceof Error ? error.message : 'No se pudo activar',
      }))
    }
  }, [refresh])

  const disable = useCallback(async () => {
    setState((s) => ({ ...s, busy: true, error: null }))
    try {
      const subscription = await currentSubscription()
      if (subscription) {
        await del('/api/push/subscribe')
        await subscription.unsubscribe()
      }
      if ('clearAppBadge' in navigator) void navigator.clearAppBadge().catch(() => {})
      await refresh()
    } finally {
      setState((s) => ({ ...s, busy: false }))
    }
  }, [refresh])

  const sendTest = useCallback(async () => {
    setState((s) => ({ ...s, busy: true, error: null }))
    try {
      const result = await post<{ sent: number }>('/api/push/test')
      if (result.sent === 0) {
        setState((s) => ({
          ...s,
          error: 'No se ha entregado en ningún dispositivo. Vuelve a activar los avisos.',
        }))
      }
    } catch (error) {
      setState((s) => ({
        ...s,
        error: error instanceof Error ? error.message : 'No se pudo enviar',
      }))
    } finally {
      setState((s) => ({ ...s, busy: false }))
    }
  }, [])

  return { ...state, enable, disable, sendTest, refresh }
}
